'use client';

import React, { useState, useEffect, Fragment, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { MdClose } from 'react-icons/md';
import Button from '@/components/ui/forms/Button';
import { ApiRoutes } from '@/settings/routes';
import Select from '@/components/ui/forms/Select';
import Switch from '@/components/ui/forms/Switch';
import InputError from '@/components/ui/alerts/InputError';
import Label from '@/components/ui/forms/Label';
import Input from '@/components/ui/forms/Input';
import Textarea from '@/components/ui/forms/Textarea';
import InputNumber from '@/components/ui/forms/InputNumber';
import { App_text } from '@/settings/AppText';
import axios from '@/lib/axios';
import { FaCheck, FaCircleChevronRight, FaRegClock, FaCalendarDays, FaMedal, FaCompass, FaStar, FaMapMarkerAlt as FaMapMarkerAlt6 } from 'react-icons/fa6';
import { FaMapMarkerAlt, FaTicketAlt } from 'react-icons/fa';
import { FaCheckCircle } from 'react-icons/fa';
import { RiCloseLine } from 'react-icons/ri';
import { BsChatDotsFill } from 'react-icons/bs';
import { IoReloadCircle } from 'react-icons/io5';
import { TiStarFullOutline } from 'react-icons/ti';
import { TbMapPin2 } from 'react-icons/tb';
import ImageWithFallback from '@/components/ImageWithFallback';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi2';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function getDaysInMonth(year, month) {
    return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfMonth(year, month) {
    return new Date(year, month, 1).getDay();
}
function isSameDay(a, b) {
    if (!a || !b) return false;
    const d1 = new Date(a);
    const d2 = new Date(b);
    return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
}

// ─── Single Date Popover ──────────────────────────────────────────────────────
function SingleDatePopover({ targetRef, selectedDate, minDate, onChange, onClose, align = 'left' }) {
    const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const min = minDate ? new Date(new Date(minDate).setHours(0, 0, 0, 0)) : today;

    const initialDate = selectedDate ? new Date(selectedDate) : min;
    const [viewYear, setViewYear] = useState(initialDate.getFullYear());
    const [viewMonth, setViewMonth] = useState(initialDate.getMonth());

    const updatePosition = useCallback(() => {
        if (targetRef.current) {
            const rect = targetRef.current.getBoundingClientRect();
            setCoords({
                top: rect.top,
                left: rect.left,
                width: rect.width,
                bottom: rect.bottom,
            });
        }
    }, [targetRef]);

    const contentRef = useRef(null);

    const [mounted, setMounted] = useState(false);
    useEffect(() => {
        setMounted(true);
        updatePosition();

        const handleOutsideClick = (e) => {
            // If click is on content or trigger, don't close
            if (contentRef.current?.contains(e.target) || targetRef.current?.contains(e.target)) {
                return;
            }
            onClose();
        };

        const handleEsc = (e) => {
            if (e.key === 'Escape') onClose();
        };

        window.addEventListener('mousedown', handleOutsideClick);
        window.addEventListener('keydown', handleEsc);
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);

        return () => {
            window.removeEventListener('mousedown', handleOutsideClick);
            window.removeEventListener('keydown', handleEsc);
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [updatePosition, onClose, targetRef]);

    const prevMonth = () => {
        if (viewMonth === 0) {
            setViewMonth(11);
            setViewYear((y) => y - 1);
        } else setViewMonth((m) => m - 1);
    };
    const nextMonth = () => {
        if (viewMonth === 11) {
            setViewMonth(0);
            setViewYear((y) => y + 1);
        } else setViewMonth((m) => m + 1);
    };

    const daysInMonth = getDaysInMonth(viewYear, viewMonth);
    const firstDay = getFirstDayOfMonth(viewYear, viewMonth);
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(viewYear, viewMonth, d));

    if (!mounted || typeof document === 'undefined') return null;

    // Calculate vertical position: prefer bottom, flip to top if needed
    const popoverHeight = 350; // estimate
    const spaceBelow = window.innerHeight - coords.bottom;
    const preferTop = spaceBelow < popoverHeight && coords.top > popoverHeight;

    const popoverStyles = {
        position: 'fixed',
        zIndex: 9999,
        top: preferTop ? coords.top - 10 : coords.bottom + 10,
        left: align === 'left' ? coords.left : coords.left + coords.width - 280,
        transform: preferTop ? 'translateY(-100%)' : 'none',
        minWidth: '280px',
        pointerEvents: 'auto',
    };

    return createPortal(
        <div
            ref={contentRef}
            className="p-5 bg-white rounded-2xl border border-gray-100 shadow-2xl"
            style={popoverStyles}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
        >
                <div className="flex justify-between items-center mb-4">
                    <button type="button" onClick={prevMonth} className="flex justify-center items-center w-8 h-8 text-gray-500 rounded-lg transition-colors hover:bg-gray-100 hover:text-gray-800">
                        <HiChevronLeft className="size-5" />
                    </button>
                    <span className="text-sm font-bold text-gray-800">
                        {MONTHS[viewMonth]} {viewYear}
                    </span>
                    <button type="button" onClick={nextMonth} className="flex justify-center items-center w-8 h-8 text-gray-500 rounded-lg transition-colors hover:bg-gray-100 hover:text-gray-800">
                        <HiChevronRight className="size-5" />
                    </button>
                </div>

                <div className="grid grid-cols-7 mb-1">
                    {DAYS.map((d) => (
                        <div key={d} className="py-1 text-[0.65rem] font-bold text-center text-gray-400 uppercase">
                            {d}
                        </div>
                    ))}
                </div>

                <div className="grid grid-cols-7">
                    {cells.map((date, i) => {
                        if (!date) return <div key={`e-${i}`} />;
                        const isPast = date < min;
                        const isSelected = isSameDay(date, selectedDate);
                        const isToday = isSameDay(date, today);

                        return (
                            <button
                                key={date.toISOString()}
                                type="button"
                                disabled={isPast}
                                onClick={() => {
                                    onChange(date.toISOString().split('T')[0]);
                                    onClose();
                                }}
                                className={`
                                    relative h-9 text-xs font-medium rounded-lg transition-all duration-150 select-none
                                    ${isPast ? 'text-gray-300 cursor-not-allowed' : 'cursor-pointer'}
                                    ${isSelected ? 'font-bold text-white' : ''}
                                    ${!isSelected && !isPast ? 'text-gray-700 hover:bg-orange-50 hover:text-main-color' : ''}
                                `}
                                style={{ backgroundColor: isSelected ? '#E8604C' : undefined }}
                            >
                                {isToday && !isSelected && <span className="absolute bottom-1 left-1/2 w-1 h-1 rounded-full -translate-x-1/2 bg-main-color" />}
                                {date.getDate()}
                            </button>
                        );
                    })}
                </div>
        </div>,
        document.body
    );
}

export default function QuoteRequestModal({ card = {}, buttonClassName = '' }) {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        date: '',
        to_date: '',
        adults: '2',
        children: '0',
        infants: '0',
        message: '',
        card_id: card.id || '',
        card_title: card.title || '',
        extras: [],
        transfer_service: false,
        terms: false,
    });
    const [submittedData, setSubmittedData] = useState(null);
    const [error, setError] = useState('');
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitResult, setSubmitResult] = useState({ success: false, message: '' });
    const [showStartCal, setShowStartCal] = useState(false);
    const [showEndCal, setShowEndCal] = useState(false);
    const [activeExtras, setActiveExtras] = useState({});
    const startRef = useRef(null);
    const endRef = useRef(null);
    const generalDateRef = useRef(null);
    const [extraTravelers, setExtraTravelers] = useState({});
    const cardExtras = Array.isArray(card?.extras) ? card.extras : [];
    const dateInputRef = useRef(null);
    const toDateInputRef = useRef(null);

    // Initialize extras state when card changes
    useEffect(() => {
        if (cardExtras.length > 0) {
            const initialActiveExtras = {};
            const initialExtraTravelers = {};

            cardExtras.forEach((extra) => {
                initialActiveExtras[extra.id] = false;
                initialExtraTravelers[extra.id] = {
                    adults: extra.adult_status ? '0' : '-1',
                    children: extra.child_status ? '0' : '-1',
                    infants: extra.infant_status ? '0' : '-1',
                };
            });

            setActiveExtras(initialActiveExtras);
            setExtraTravelers(initialExtraTravelers);
        }
    }, [card?.id]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleExtraToggle = (extraId) => {
        const newActiveExtras = { ...activeExtras };
        newActiveExtras[extraId] = !newActiveExtras[extraId];
        setActiveExtras(newActiveExtras);

        let updatedExtras = [...formData.extras];
        if (newActiveExtras[extraId]) {
            if (!updatedExtras.includes(extraId)) {
                updatedExtras.push(extraId);
            }
        } else {
            updatedExtras = updatedExtras.filter((id) => id !== extraId);
        }

        setFormData((prev) => ({
            ...prev,
            extras: updatedExtras,
        }));
    };

    const handleExtraTravelerChange = (extraId, type, value) => {
        const extra = cardExtras.find((e) => e.id === extraId);
        if (!extra) return;

        if ((type === 'adults' && !extra.adult_status) || (type === 'children' && !extra.child_status) || (type === 'infants' && !extra.infant_status)) {
            return;
        }

        const mainCount = parseInt(formData[type]) || 0;
        let newValue = parseInt(value) || 0;

        if (newValue > mainCount) {
            newValue = mainCount;
        }

        setExtraTravelers((prev) => ({
            ...prev,
            [extraId]: {
                ...prev[extraId],
                [type]: newValue.toString(),
            },
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name) newErrors.name = ['Name is required'];
        if (!formData.email) newErrors.email = ['Email is required'];
        if (!formData.phone) newErrors.phone = ['Whatsapp is required'];
        if (!formData.date) newErrors.date = ['Travel date is required'];
        if (card.type === 'private' && !formData.to_date) newErrors.to_date = ['Return date is required'];

        const adultCount = parseInt(formData.adults);
        if (adultCount < 2) {
            newErrors.adults = ['At least 2 adults are required'];
        } else if (adultCount > 16) {
            newErrors.adults = ['Maximum 16 adults allowed'];
        }

        const childCount = parseInt(formData.children);
        if (childCount > 9) {
            newErrors.children = ['Maximum 9 children allowed'];
        }

        const infantCount = parseInt(formData.infants);
        if (infantCount > 9) {
            newErrors.infants = ['Maximum 9 infants allowed'];
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Calculate pricing
    const adultPrice = card.has_promo ? parseFloat(card.price_promo) : parseFloat(card.price);
    const childPrice = (card.has_promo ? parseFloat(card.price_promo) : parseFloat(card.price)) * 0.8;
    const infantPrice = 0;

    const adultTotal = parseFloat((parseInt(formData.adults) * adultPrice).toFixed(2));
    const childTotal = parseFloat((parseInt(formData.children) * childPrice).toFixed(2));
    const infantTotal = parseFloat((parseInt(formData.infants) * infantPrice).toFixed(2));

    const mainActivityTotal = parseFloat((adultTotal + childTotal + infantTotal).toFixed(2));

    const selectedExtraIds = new Set((Array.isArray(formData?.extras) ? formData.extras : []).map((id) => String(id)));
    const extrasDetails = cardExtras
        .filter((extra) => selectedExtraIds.has(String(extra.id)))
        .map((extra) => {
            const adultCount = parseInt(extraTravelers[extra.id]?.adults || 0);
            const childCount = parseInt(extraTravelers[extra.id]?.children || 0);
            const infantCount = parseInt(extraTravelers[extra.id]?.infants || 0);

            const adultExtraPrice = extra.adult_status ? parseFloat(extra.price) : 0;
            const childExtraPrice = extra.child_status ? parseFloat(extra.price) : 0;
            const infantExtraPrice = extra.infant_status ? parseFloat(extra.price) : 0;

            const adultExtraTotal = parseFloat((adultCount * adultExtraPrice).toFixed(2));
            const childExtraTotal = parseFloat((childCount * childExtraPrice).toFixed(2));
            const infantExtraTotal = parseFloat((infantCount * infantExtraPrice).toFixed(2));

            const extraTotal = parseFloat((adultExtraTotal + childExtraTotal + infantExtraTotal).toFixed(2));

            return {
                id: extra.id,
                name: extra.name,
                price: parseFloat(extra.price),
                adult_count: adultCount,
                child_count: childCount,
                infant_count: infantCount,
                adult_price: adultExtraPrice,
                child_price: childExtraPrice,
                infant_price: infantExtraPrice,
                adult_total: adultExtraTotal,
                child_total: childExtraTotal,
                infant_total: infantExtraTotal,
                total: extraTotal,
            };
        });

    const extrasTotal = parseFloat(extrasDetails.reduce((sum, extra) => sum + extra.total, 0).toFixed(2));
    const grandTotal = parseFloat((mainActivityTotal + extrasTotal).toFixed(2));
    const depositAmount = parseFloat((grandTotal * 0.3).toFixed(2));

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            setSubmitResult({
                success: false,
                message: App_text.form_package.form_error_message,
            });
            return;
        }

        setIsSubmitting(true);
        setSubmitResult({ success: false, message: '' });

        // Your existing submission logic here...
        const extrasAsIntegers = (Array.isArray(formData?.extras) ? formData.extras : []).map((id) => parseInt(id));

        let formattedDate = formData.date;
        let fromDate = null;
        let toDate = null;
        let displayFormattedDate = null;

        if (card.type === 'private' && formData.date) {
            formattedDate = formData.date;
            toDate = formData.to_date || null;
            displayFormattedDate = `${formatDateWithSuffix(formData.date)}${toDate ? ` to ${formatDateWithSuffix(toDate)}` : ''}`;
        } else if (card.type !== 'private' && card.dates && formData.date) {
            const selectedDate = card.dates.find((date) => date.id.toString() === formData.date.toString());
            if (selectedDate) {
                fromDate = selectedDate.from_date || selectedDate.date;
                toDate = selectedDate.to_date || null;
                formattedDate = fromDate;
                displayFormattedDate = `${formatDateWithSuffix(fromDate)}${toDate ? ` to ${formatDateWithSuffix(toDate)}` : ''}`;
            }
        }

        const pricingSummary = {
            main_activity: {
                adult_price: adultPrice,
                child_price: childPrice,
                infant_price: infantPrice,
                adult_count: parseInt(formData.adults),
                child_count: parseInt(formData.children),
                infant_count: parseInt(formData.infants),
                adult_total: adultTotal,
                child_total: childTotal,
                infant_total: infantTotal,
                total: mainActivityTotal,
            },
            extras: extrasDetails,
            extras_total: extrasTotal,
            grand_total: grandTotal,
            deposit: depositAmount,
        };

        let startingPointsText = '';
        if (card.starting_points && card.starting_points.length > 0) {
            startingPointsText = card.starting_points.map((point) => point.name).join(', ');
        }

        if (!displayFormattedDate) {
            if (formattedDate) {
                displayFormattedDate = formatDateWithSuffix(formattedDate);
            } else {
                displayFormattedDate = 'N/A';
            }
        }

        const submissionData = {
            ...formData,
            date: formattedDate,
            from_date: fromDate,
            to_date: toDate,
            formattedDate: displayFormattedDate,
            extras: extrasAsIntegers,
            extraTravelers: extraTravelers,
            pricing_summary: pricingSummary,
            card_type: card.type,
            starting_points: card.starting_points || [],
            duration: card.duration || null,
            starting_point: startingPointsText,
        };

        try {
            let recaptcha_token = null;
            if (typeof window !== 'undefined' && window.grecaptcha && window.grecaptcha.execute) {
                try {
                    recaptcha_token = await window.grecaptcha.execute(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || '6LeaqwssAAAAAC_kyTDxqmP0MagTEBF8Co7mexcs', { action: 'quote' });
                } catch (e) {
                    recaptcha_token = null;
                }
            }
            const response = await axios.post(ApiRoutes.API_submit_quote_request, { ...submissionData, recaptcha_token });

            if (response.data.status === 200) {
                setSubmittedData({
                    ...formData,
                    extraTravelers: extraTravelers,
                    formattedDate: formattedDate,
                    pricing_summary: pricingSummary,
                    starting_point: startingPointsText,
                    duration: card.duration,
                });

                setSubmitResult({
                    success: true,
                    message: response.data.message || 'Your quote request has been submitted successfully. We will contact you shortly.',
                });

                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    date: '',
                    to_date: '',
                    adults: '2',
                    children: '0',
                    infants: '0',
                    message: '',
                    card_id: card.id || '',
                    card_title: card.title || '',
                    extras: [],
                    transfer_service: false,
                    terms: false,
                });
                setActiveExtras({});
                setExtraTravelers({});
            } else {
                setSubmitResult({
                    success: false,
                    message: response.data.message || 'There was an error submitting your request. Please try again.',
                });
                if (response.data.errors) {
                    setErrors(response.data.errors);
                }
            }
        } catch (error) {
            console.error('Error submitting quote request:', error);

            if (error.response) {
                console.error('Status:', error.response.status);
                console.error('Data:', error.response.data);

                if (error.response.status === 401 && error.response.data.errors) {
                    setSubmitResult({
                        success: false,
                        message: error.response.data.message || 'Please check the form for errors.',
                    });
                    setErrors(error.response.data.errors);
                } else {
                    setSubmitResult({
                        success: false,
                        message: error.response.data.message || 'There was an error submitting your request. Please try again.',
                    });
                    setErrors({});
                }
            } else {
                setSubmitResult({
                    success: false,
                    message: 'Unable to connect to the server. Please check your internet connection and try again.',
                });
                setErrors({});
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const getThumbnail = () => {
        const thumb = card?.thumbnail;
        const packageImages = Array.isArray(card?.images) ? card.images : [];
        const thumbFromImages = packageImages.find((img) => img?.thumbnail) || packageImages[0];

        if (typeof thumb === 'string' && thumb) return thumb.startsWith('http') ? thumb : ApiRoutes.ASSETS_ENDPOINT + thumb;
        if (thumb?.url) return thumb.url.startsWith('http') ? thumb.url : ApiRoutes.ASSETS_ENDPOINT + thumb.url;
        if (thumbFromImages?.url) return thumbFromImages.url.startsWith('http') ? thumbFromImages.url : ApiRoutes.ASSETS_ENDPOINT + thumbFromImages.url;
        
        return App_text.defaults.images.tour_card;
    };

    const formatDateWithSuffix = (dateString) => {
        const date = new Date(dateString);
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return date.toLocaleDateString('en-US', options).replace(/(\d+),/, (match, day) => {
            const suffix = day.endsWith('1') && !day.endsWith('11') ? 'st' : day.endsWith('2') && !day.endsWith('12') ? 'nd' : day.endsWith('3') && !day.endsWith('13') ? 'rd' : 'th';
            return `${day}${suffix},`;
        });
    };

    const changeInput = (name, val) => {
        let newValue = Array.isArray(val) && val.length === 1 ? parseFloat(val[0]) : val;

        if (name === 'adults') {
            newValue = Math.max(2, Math.min(16, parseInt(newValue)));
        } else if (name === 'children' || name === 'infants') {
            newValue = Math.max(0, Math.min(9, parseInt(newValue)));
        }

        setFormData({ ...formData, [name]: newValue.toString() });
    };

    const totalTravelers = parseInt(formData.adults || 0) + parseInt(formData.children || 0) + parseInt(formData.infants || 0);

    const infoClass = 'text-sm text-gray-500';

    return (
        <Dialog.Root>
            <Dialog.Trigger asChild>
                <button
                    type="button"
                    className={`inline-flex items-center justify-center gap-2 font-semibold text-sm text-white rounded-full border-none cursor-pointer transition-all duration-200 hover:opacity-90 hover:-translate-y-0.5 ${buttonClassName}`}
                    style={{ background: 'linear-gradient(135deg, #E8604C 0%, #313041 100%)', boxShadow: '0 4px 14px rgba(232,96,76,0.35)' }}
                >{'Request A Quote'}
                    <FaCircleChevronRight />
                </button>
            </Dialog.Trigger>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-[51] backdrop-blur-sm bg-black/60 DialogOverlay" />
                <Dialog.Content className="overflow-hidden fixed top-1/2 left-1/2 z-[52] w-[95vw] max-w-7xl h-[90vh] bg-white shadow-2xl rounded-2xl -translate-x-1/2 -translate-y-1/2 DialogContent">
                    <Dialog.Title className="sr-only">{submitResult.success ? 'Booking Confirmation' : 'Request a Quote'}</Dialog.Title>
                    {!submitResult.success ? (
                        <div className="grid overflow-y-scroll h-full md:grid-cols-5">
                            {/* Left Sidebar - Card Info */}
                            <div className="hidden relative col-span-2 bg-gradient-to-br from-orange-50 to-orange-100/50 md:block">
                                <Dialog.Close asChild>
                                    <button className="flex absolute top-4 right-4 z-10 justify-center items-center text-gray-700 bg-white rounded-full shadow-md transition-all duration-300 size-10 hover:bg-orange-600 hover:text-white focus:outline-none">
                                        <MdClose className="size-5" />
                                    </button>
                                </Dialog.Close>

                                <div className="overflow-y-auto h-full">
                                    <div className="p-8">
                                        {/* Card Image */}
                                        <div className="overflow-hidden relative mb-6 h-60 rounded-2xl border-2 border-white shadow-lg">
                                            <ImageWithFallback src={getThumbnail()} alt={card.title} className="object-cover w-full h-full" />
                                            
                                            {/* Starting Point Badge */}
                                            {(Array.isArray(card.destinations) && card.destinations.length > 0) || card.start_destination ? (
                                                <div className="absolute z-10 top-4 left-4 flex items-center gap-1.5 px-3 py-1.5 bg-white/95 backdrop-blur-md rounded-full shadow-xl border border-orange-100">
                                                    <FaMapMarkerAlt className="text-[#E8604C] size-3.5" />
                                                    <span className="text-[11px] font-bold text-gray-800 uppercase tracking-tight">
                                                        {Array.isArray(card.destinations) && card.destinations.length > 0 
                                                            ? (card.destinations[0]?.name || card.destinations[0]) 
                                                            : (typeof card.start_destination === 'object' ? card.start_destination.name : card.start_destination)}
                                                    </span>
                                                </div>
                                            ) : (
                                                <div className="absolute z-10 top-5 left-5 flex items-center gap-1.5 px-2.5 py-1.5 bg-white/95 backdrop-blur-sm rounded-lg shadow-md">
                                                    <span className="text-xs font-bold text-[#E8604C] uppercase tracking-wide">Private</span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="p-6 mb-6 bg-white rounded-xl border border-orange-200">
                                            {/* Title */}
                                            <h2 className="pb-4 mb-4 text-2xl font-bold text-gray-900 border-b border-orange-100">{card.title}</h2>

                                            {/* Tour Info */}
                                            <div className="flex flex-col gap-2 pb-4 mb-4 border-b border-orange-100">
                                                {/* Duration and Price aligned */}
                                                <div className="flex flex-col gap-3 justify-between w-full sm:flex-row sm:items-center">
                                                    {card.duration && (
                                                        <div className="flex gap-2 items-center text-gray-700">
                                                            <FaCalendarDays className="text-[#E8604C] size-4 shrink-0" />
                                                            <span className="text-sm font-semibold">{card.duration}</span>
                                                        </div>
                                                    )}
                                                    {card.price && (
                                                        <div className="flex gap-2 items-center text-gray-700">
                                                            <FaTicketAlt className="text-[#E8604C] size-4 shrink-0" />
                                                            <span className="text-sm font-semibold">
                                                                from {App_text.generale.currancy}
                                                                {card.has_promo ? card.price_promo : card.price} /per
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Confidence Section */}
                                            <span className="text-[1.2rem] font-bold text-gray-700">Plan Your Trip With Confidence</span>
                                            <div className="mt-3 space-y-2">
                                                <div className="flex gap-3 items-center text-sm text-gray-600">
                                                    <FaCheckCircle className="flex-shrink-0 text-orange-500 size-4" />
                                                    <span>Free &amp; no-obligation quote</span>
                                                </div>
                                                <div className="flex gap-3 items-center text-sm text-gray-600">
                                                    <BsChatDotsFill className="flex-shrink-0 text-orange-500 size-4" />
                                                    <span>Reply within 24 hours</span>
                                                </div>
                                                <div className="flex gap-3 items-center text-sm text-gray-600">
                                                    <FaMedal className="flex-shrink-0 text-orange-500 size-4" />
                                                    <span>10+ years of experience</span>
                                                </div>
                                                <div className="flex gap-3 items-center text-sm text-gray-600">
                                                    <IoReloadCircle className="flex-shrink-0 text-orange-500 size-4" />
                                                    <span>Free cancellation</span>
                                                </div>
                                                <div className="flex gap-3 items-center text-sm text-gray-600">
                                                    <FaCompass className="flex-shrink-0 text-orange-500 size-4" />
                                                    <span>Local Moroccan guides</span>
                                                </div>
                                                <div className="flex gap-3 items-center text-sm text-gray-600">
                                                    <FaStar className="flex-shrink-0 text-orange-500 size-4" />
                                                    <span>Best Morocco Experience, Guaranteed</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right Side - Form */}
                            <div className="overflow-y-auto col-span-3 p-4 md:p-8">
                                {/* Mobile Close Button */}
                                <div className="flex justify-between items-center mb-2 md:hidden">
                                    <h3 className="text-xl font-bold text-gray-900 md:text-2xl">{App_text.form_package.form_title}</h3>
                                    <Dialog.Close asChild>
                                        <button className="flex justify-center items-center text-gray-700 bg-gray-100 rounded-full transition-all duration-300 size-10 hover:bg-orange-600 hover:text-white focus:outline-none">
                                            <MdClose className="size-5" />
                                        </button>
                                    </Dialog.Close>
                                </div>

                                {/* Package Title on Mobile */}
                                <p className="block mb-6 text-base font-semibold text-gray-700 md:hidden">{card.title}</p>

                                {/* Desktop Title */}
                                <h3 className="hidden mb-6 text-3xl font-bold text-gray-900 md:block">Request a Quote</h3>

                                {/* Error Summary */}
                                {Object.keys(errors).length > 0 && submitResult.message && !submitResult.success && (
                                    <div className="p-4 mb-6 bg-red-50 rounded-xl border border-red-200">
                                        <h4 className="mb-2 font-semibold text-red-800">{submitResult.message}</h4>
                                        <ul className="pl-5 space-y-1 text-sm list-disc text-red-700">
                                            {Object.entries(errors).map(([field, errorMessages]) => (
                                                <li key={field}>
                                                    {field.charAt(0).toUpperCase() + field.slice(1)}: {Array.isArray(errorMessages) ? errorMessages[0] : errorMessages}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                <form onSubmit={handleSubmit} className="space-y-6">
                                    {/* Contact Information */}
                                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 md:p-6">
                                        <h4 className="mb-4 text-lg font-semibold text-gray-900">Contact Information</h4>
                                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                            <div>
                                                <Label htmlFor="name" className={errors.name ? 'text-red-500' : ''}>
                                                    {App_text.form_package.full_name} *
                                                </Label>
                                                <Input
                                                    onChange={handleChange}
                                                    value={formData.name}
                                                    name="name"
                                                    type="text"
                                                    placeholder="Full Name"
                                                    className={`${errors.name ? 'border-red-500 bg-red-50' : ''}`}
                                                    required
                                                />
                                                {errors.name && errors.name.map((error, index) => <InputError key={index} message={error} />)}
                                            </div>

                                            <div>
                                                <Label htmlFor="email" className={errors.email ? 'text-red-500' : ''}>
                                                    {App_text.form_package.email} *
                                                </Label>
                                                <Input
                                                    type="email"
                                                    id="email"
                                                    name="email"
                                                    placeholder="example@mail.com"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    required
                                                    className={`${errors.email ? 'border-red-500 bg-red-50' : ''}`}
                                                />
                                                {errors.email && errors.email.map((error, index) => <InputError key={index} message={error} />)}
                                            </div>

                                            <div>
                                                <Label htmlFor="phone">{App_text.form_package.whatsapp} *</Label>
                                                <Input type="tel" id="phone" name="phone" placeholder="(000) 000-0000" value={formData.phone} onChange={handleChange} />
                                                {errors.phone && errors.phone.map((error, index) => <InputError key={index} message={error} />)}
                                            </div>
                                        </div>

                                        <div className="mt-4">
                                            {card.type === 'private' ? (
                                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                                    <div className="relative group" ref={startRef}>
                                                        <div
                                                            onClick={() => setShowStartCal(!showStartCal)}
                                                            className={`cursor-pointer flex flex-col gap-1 px-4 py-3 w-full text-left rounded-xl border transition-all duration-200 ${errors.date ? 'border-red-500 bg-red-50 text-red-900' : 'border-gray-200 hover:border-main-color hover:bg-gray-50'}`}
                                                        >
                                                            <span className="text-[10px] font-bold tracking-wider text-gray-500 uppercase">{App_text.form_package.travel_date}</span>
                                                            <div className="flex gap-2 items-center">
                                                                <FaCalendarDays className="text-main-color size-3.5 shrink-0" />
                                                                <span className={`text-sm font-semibold ${formData.date ? 'text-gray-800' : 'text-gray-400'}`}>
                                                                    {formData.date ? formatDateWithSuffix(formData.date) : 'Start date'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        {showStartCal && (
                                                            <SingleDatePopover
                                                                targetRef={startRef}
                                                                selectedDate={formData.date}
                                                                minDate={null}
                                                                onChange={(date) => {
                                                                    setFormData((prev) => ({ ...prev, date }));
                                                                    if (formData.to_date && new Date(date) > new Date(formData.to_date)) {
                                                                        setFormData((prev) => ({ ...prev, to_date: '' }));
                                                                    }
                                                                }}
                                                                onClose={() => setShowStartCal(false)}
                                                            />
                                                        )}
                                                        {errors.date && errors.date.map((error, index) => <InputError key={index} message={error} />)}
                                                    </div>

                                                    <div className="relative group" ref={endRef}>
                                                        <div
                                                            onClick={() => setShowEndCal(!showEndCal)}
                                                            className={`cursor-pointer flex flex-col gap-1 px-4 py-3 w-full text-left rounded-xl border transition-all duration-200 ${errors.to_date ? 'border-red-500 bg-red-50 text-red-900' : 'border-gray-200 hover:border-main-color hover:bg-gray-50'}`}
                                                        >
                                                            <span className="text-[10px] font-bold tracking-wider text-gray-500 uppercase">{App_text.form_package.date} (To)</span>
                                                            <div className="flex gap-2 items-center">
                                                                <FaCalendarDays className="text-main-color size-3.5 shrink-0" />
                                                                <span className={`text-sm font-semibold ${formData.to_date ? 'text-gray-800' : 'text-gray-400'}`}>
                                                                    {formData.to_date ? formatDateWithSuffix(formData.to_date) : 'End date'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        {showEndCal && (
                                                            <SingleDatePopover
                                                                targetRef={endRef}
                                                                selectedDate={formData.to_date}
                                                                minDate={formData.date ? new Date(formData.date) : new Date()}
                                                                onChange={(date) => setFormData((prev) => ({ ...prev, to_date: date }))}
                                                                onClose={() => setShowEndCal(false)}
                                                                align="right"
                                                            />
                                                        )}
                                                        {errors.to_date && errors.to_date.map((error, index) => <InputError key={index} message={error} />)}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                                    <div className="relative group" ref={startRef}>
                                                        <div
                                                            onClick={() => setShowStartCal(!showStartCal)}
                                                            className={`cursor-pointer flex flex-col gap-1 px-4 py-3 w-full text-left rounded-xl border transition-all duration-200 ${errors.date ? 'border-red-500 bg-red-50 text-red-900' : 'border-gray-200 hover:border-main-color hover:bg-gray-50'}`}
                                                        >
                                                            <span className="text-[10px] font-bold tracking-wider text-gray-500 uppercase">START DATE</span>
                                                            <div className="flex gap-2 items-center">
                                                                <FaCalendarDays className="text-main-color size-3.5 shrink-0" />
                                                                <span className={`text-sm font-semibold ${formData.date ? 'text-gray-800' : 'text-gray-400'}`}>
                                                                    {formData.date ? formatDateWithSuffix(formData.date) : 'Add date'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        {showStartCal && (
                                                            <SingleDatePopover
                                                                targetRef={startRef}
                                                                selectedDate={formData.date}
                                                                minDate={null}
                                                                onChange={(date) => {
                                                                    setFormData((prev) => ({ ...prev, date }));
                                                                    if (formData.to_date && new Date(date) > new Date(formData.to_date)) {
                                                                        setFormData((prev) => ({ ...prev, to_date: '' }));
                                                                    }
                                                                }}
                                                                onClose={() => setShowStartCal(false)}
                                                            />
                                                        )}
                                                        {errors.date && errors.date.map((error, index) => <InputError key={index} message={error} />)}
                                                    </div>

                                                    <div className="relative group" ref={endRef}>
                                                        <div
                                                            onClick={() => setShowEndCal(!showEndCal)}
                                                            className={`cursor-pointer flex flex-col gap-1 px-4 py-3 w-full text-left rounded-xl border transition-all duration-200 ${errors.to_date ? 'border-red-500 bg-red-50 text-red-900' : 'border-gray-200 hover:border-main-color hover:bg-gray-50'}`}
                                                        >
                                                            <span className="text-[10px] font-bold tracking-wider text-gray-500 uppercase">END DATE</span>
                                                            <div className="flex gap-2 items-center">
                                                                <FaCalendarDays className="text-main-color size-3.5 shrink-0" />
                                                                <span className={`text-sm font-semibold ${formData.to_date ? 'text-gray-800' : 'text-gray-400'}`}>
                                                                    {formData.to_date ? formatDateWithSuffix(formData.to_date) : 'Add date'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        {showEndCal && (
                                                            <SingleDatePopover
                                                                targetRef={endRef}
                                                                selectedDate={formData.to_date}
                                                                minDate={formData.date ? new Date(formData.date) : new Date()}
                                                                onChange={(date) => setFormData((prev) => ({ ...prev, to_date: date }))}
                                                                onClose={() => setShowEndCal(false)}
                                                                align="right"
                                                            />
                                                        )}
                                                        {errors.to_date && errors.to_date.map((error, index) => <InputError key={index} message={error} />)}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Travelers */}
                                    <div className="p-4 bg-gradient-to-br from-orange-50 rounded-xl border border-orange-200 md:p-6 to-orange-100/30">
                                        <h4 className="mb-4 text-lg font-semibold text-gray-900">{App_text.form_package.guests_title}</h4>
                                        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                                            <div>
                                                <div className="mb-1">
                                                    <Label htmlFor="adults" className={`${errors.adults ? 'text-red-500' : ''} mb-0`}>
                                                        {App_text.form_package.adult_label}
                                                    </Label>
                                                    <span className={`text-xs ${errors.adults ? 'text-red-500' : 'text-gray-500'}`}>{App_text.form_package.adult_limits}</span>
                                                </div>
                                                <InputNumber
                                                    id="adults"
                                                    name="adults"
                                                    defaultValue={formData.adults}
                                                    changeInput={(val) => changeInput('adults', val)}
                                                    min={2}
                                                    max={16}
                                                    required
                                                    className={errors.adults ? 'border-red-500 bg-red-50' : ''}
                                                />
                                                {errors.adults && errors.adults.map((error, index) => <InputError key={index} message={error} />)}
                                            </div>

                                            <div>
                                                <div className="mb-1">
                                                    <Label htmlFor="children" className={`mb-0 ${errors.children ? 'text-red-500' : ''}`}>
                                                        {App_text.form_package.child_label}
                                                    </Label>
                                                    <span className={`text-xs ${errors.children ? 'text-red-500' : 'text-gray-500'}`}>{App_text.form_package.child_limits}</span>
                                                </div>
                                                <InputNumber
                                                    id="children"
                                                    name="children"
                                                    defaultValue={formData.children}
                                                    changeInput={(val) => changeInput('children', val)}
                                                    min={0}
                                                    max={9}
                                                    className={errors.children ? 'border-red-500 bg-red-50' : ''}
                                                />
                                                {errors.children && errors.children.map((error, index) => <InputError key={index} message={error} />)}
                                            </div>

                                            <div>
                                                <div className="mb-1">
                                                    <Label htmlFor="infants" className={`mb-0 ${errors.infants ? 'text-red-500' : ''}`}>
                                                        {App_text.form_package.infant_label}
                                                    </Label>
                                                    <span className={`text-xs ${errors.infants ? 'text-red-500' : 'text-gray-500'}`}>{App_text.form_package.infant_limits}</span>
                                                </div>
                                                <InputNumber
                                                    id="infants"
                                                    name="infants"
                                                    defaultValue={formData.infants}
                                                    changeInput={(val) => changeInput('infants', val)}
                                                    min={0}
                                                    max={9}
                                                    className={errors.infants ? 'border-red-500 bg-red-50' : ''}
                                                />
                                                {errors.infants && errors.infants.map((error, index) => <InputError key={index} message={error} />)}
                                            </div>
                                        </div>

                                        <div className="flex gap-2 justify-center items-center p-3 mt-4 bg-white rounded-lg border border-orange-200">
                                            <span className="text-sm text-gray-600">Total Travelers:</span>
                                            <span className="text-xl font-bold text-orange-600">{totalTravelers}</span>
                                        </div>
                                    </div>

                                    {/* Extras Activities */}
                                    {cardExtras.length > 0 && (
                                        <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 md:p-6">
                                            <div className="flex justify-between items-center mb-4">
                                                <div>
                                                    <h4 className="text-lg font-semibold text-gray-900">{App_text.form_package.extras_title}</h4>
                                                    <p className="text-sm text-gray-500">{App_text.form_package.extras_description}</p>
                                                </div>
                                                <Switch
                                                    id="want-extras"
                                                    checked={formData.extras.length > 0}
                                                    onChange={(e) => {
                                                        if (!e.target.checked) {
                                                            const newActiveExtras = {};
                                                            cardExtras.forEach((extra) => {
                                                                newActiveExtras[extra.id] = false;
                                                            });
                                                            setActiveExtras(newActiveExtras);
                                                            setFormData((prev) => ({ ...prev, extras: [] }));
                                                        } else {
                                                            if (cardExtras.length > 0) {
                                                                const firstExtra = cardExtras[0];
                                                                const newActiveExtras = { ...activeExtras };
                                                                newActiveExtras[firstExtra.id] = true;
                                                                setActiveExtras(newActiveExtras);
                                                                setFormData((prev) => ({ ...prev, extras: [firstExtra.id] }));
                                                            }
                                                        }
                                                    }}
                                                />
                                            </div>

                                            {formData.extras.length > 0 && (
                                                <div className="mt-4 space-y-4">
                                                    <h5 className="font-medium text-gray-900">{App_text.form_package.extras_options}</h5>
                                                    <p className="text-sm text-gray-500">{App_text.form_package.extras_select}</p>

                                                    {cardExtras.map((extra) => (
                                                        <div key={extra.id} className="p-4 bg-white rounded-lg border border-gray-200">
                                                            <div className="flex justify-between items-center">
                                                                <div>
                                                                    <h6 className="font-medium text-gray-900">{extra.name}</h6>
                                                                    <span className="text-sm text-gray-600">
                                                                        <span className="font-semibold text-orange-600">
                                                                            {extra.price} {App_text.generale.currancy}
                                                                        </span>{' '}
                                                                        {App_text.form_package.extras_per_person}
                                                                    </span>
                                                                </div>
                                                                <Switch id={`extra-${extra.id}`} checked={activeExtras[extra.id] || false} onChange={() => handleExtraToggle(extra.id)} />
                                                            </div>

                                                            <div className={activeExtras[extra.id] ? 'mt-4' : 'hidden'}>
                                                                <hr className="mb-4" />
                                                                {!extra.adult_status && !extra.child_status && !extra.infant_status ? (
                                                                    <div className="p-4 text-center text-amber-700 bg-amber-50 rounded border border-amber-200">
                                                                        {App_text.form_package.extras_not_available}
                                                                    </div>
                                                                ) : (
                                                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                                                                        <div>
                                                                            <Label htmlFor={`extra-${extra.id}-adults`}>{App_text.form_package.extras_adults}</Label>
                                                                            {extra.adult_status ? (
                                                                                <InputNumber
                                                                                    id={`extra-${extra.id}-adults`}
                                                                                    defaultValue={extraTravelers[extra.id]?.adults || '0'}
                                                                                    changeInput={(val) => handleExtraTravelerChange(extra.id, 'adults', val)}
                                                                                    min={0}
                                                                                    max={formData.adults}
                                                                                />
                                                                            ) : (
                                                                                <div className="px-3 py-2 text-sm text-gray-500 bg-gray-50 rounded border border-gray-200">
                                                                                    {App_text.form_package.extras_not_available_adults}
                                                                                </div>
                                                                            )}
                                                                        </div>

                                                                        <div>
                                                                            <Label htmlFor={`extra-${extra.id}-children`}>{App_text.form_package.extras_children}</Label>
                                                                            {extra.child_status ? (
                                                                                <InputNumber
                                                                                    id={`extra-${extra.id}-children`}
                                                                                    defaultValue={extraTravelers[extra.id]?.children || '0'}
                                                                                    changeInput={(val) => handleExtraTravelerChange(extra.id, 'children', val)}
                                                                                    min={0}
                                                                                    max={formData.children}
                                                                                />
                                                                            ) : (
                                                                                <div className="px-3 py-2 text-sm text-gray-500 bg-gray-50 rounded border border-gray-200">
                                                                                    {App_text.form_package.extras_not_available_children}
                                                                                </div>
                                                                            )}
                                                                        </div>

                                                                        <div>
                                                                            <Label htmlFor={`extra-${extra.id}-infants`}>{App_text.form_package.extras_infants}</Label>
                                                                            {extra.infant_status ? (
                                                                                <InputNumber
                                                                                    id={`extra-${extra.id}-infants`}
                                                                                    defaultValue={extraTravelers[extra.id]?.infants || '0'}
                                                                                    changeInput={(val) => handleExtraTravelerChange(extra.id, 'infants', val)}
                                                                                    min={0}
                                                                                    max={formData.infants}
                                                                                />
                                                                            ) : (
                                                                                <div className="px-3 py-2 text-sm text-gray-500 bg-gray-50 rounded border border-gray-200">
                                                                                    {App_text.form_package.extras_not_available_infants}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Additional Comments */}
                                    <div>
                                        <Label htmlFor="message">{App_text.form_package.additional_comments}</Label>
                                        <Textarea rows={4} onChange={handleChange} value={formData.message} name="message" type="text" placeholder={App_text.form_package.comments_placeholder} />
                                        {errors.message && errors.message.map((error, index) => <InputError key={index} message={error} />)}
                                    </div>

                                    {/* Contact Information */}
                                    <div className="flex gap-3 items-start p-4 bg-blue-50 rounded-xl border border-blue-200">
                                        <BsChatDotsFill className="flex-shrink-0 mt-0.5 text-blue-600 size-5" />
                                        <p className="text-sm text-gray-700">Our team will contact you within 24 hours to discuss all details and finalize your booking.</p>
                                    </div>

                                    {/* Submit Buttons */}
                                    <div className="flex gap-3 justify-end pt-4">
                                        <Dialog.Close asChild>
                                            <button
                                                type="button"
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '8px',
                                                    padding: '10px 24px',
                                                    borderRadius: '9999px',
                                                    background: '#ffffff',
                                                    color: '#313041',
                                                    fontSize: '0.875rem',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    border: 'none',
                                                    position: 'relative',
                                                    transition: 'color 0.25s ease, transform 0.2s ease',
                                                    minWidth: '96px',
                                                    height: '42px',
                                                }}
                                                className="cancel-btn-outline"
                                            >
                                                {App_text.form_package.cancel_button}
                                            </button>
                                        </Dialog.Close>

                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="inline-flex items-center justify-center gap-2 font-semibold text-sm text-white rounded-full border-none cursor-pointer transition-all duration-200 hover:opacity-90 hover:-translate-y-0.5"
                                            style={{
                                                background: 'linear-gradient(135deg, #E8604C 0%, #313041 100%)',
                                                boxShadow: '0 4px 14px rgba(232,96,76,0.35)',
                                                padding: '10px 28px',
                                                height: '42px',
                                                minWidth: '140px',
                                            }}
                                        >
                                            {isSubmitting ? App_text.form_package.submitting_button : App_text.form_package.submit_button}
                                            <FaCircleChevronRight />
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    ) : (
                        // SUCCESS STATE - REPLACE THIS ENTIRE SECTION
                        <div className="overflow-y-auto h-full">
                            <div className="relative min-h-full bg-gradient-to-br from-green-50 via-white to-orange-50">
                                {/* Close Button */}
                                <div className="flex absolute top-6 right-6 z-10">
                                    <Dialog.Close asChild>
                                        <button className="flex justify-center items-center text-gray-700 bg-white rounded-full shadow-lg transition-all duration-300 size-12 hover:bg-orange-600 hover:text-white hover:shadow-xl focus:outline-none">
                                            <MdClose className="size-6" />
                                        </button>
                                    </Dialog.Close>
                                </div>

                                <div className="px-8 py-12">
                                    {/* Success Header */}
                                    <div className="mx-auto mb-12 max-w-3xl">
                                        <div className="flex flex-col gap-6 items-center text-center">
                                            {/* Success Icon with Animation */}
                                            <div className="relative">
                                                <div className="flex absolute inset-0 justify-center items-center bg-green-100 rounded-full opacity-25 animate-ping"></div>
                                                <div className="flex relative justify-center items-center w-24 h-24 text-white bg-gradient-to-br from-green-500 to-green-600 rounded-full shadow-xl">
                                                    <FaCheckCircle className="size-14" />
                                                </div>
                                            </div>

                                            {/* Success Message */}
                                            <div className="space-y-3">
                                                <h1 className="text-4xl font-bold text-gray-900 md:text-5xl">
                                                    {App_text.pages.thank_you.main_message}
                                                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-orange-500"> {submittedData?.name}</span>
                                                    {App_text.pages.thank_you.main_message_2}
                                                </h1>
                                                <p className="text-lg text-gray-600 md:text-xl">{App_text.pages.thank_you.description}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Content Cards */}
                                    <div className="mx-auto space-y-6 max-w-4xl">
                                        {card.type === 'private' ? (
                                            <>
                                                {/* Private Card - Reservation Details */}
                                                <div className="overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-lg">
                                                    <div className="p-6 bg-gradient-to-r from-orange-50 border-b border-orange-200 to-orange-100/50">
                                                        <h3 className="flex gap-2 items-center text-xl font-bold text-gray-900">
                                                            <div className="p-2 bg-orange-200 rounded-lg">
                                                                <FaCheck className="text-orange-700 size-5" />
                                                            </div>
                                                            {App_text.form_package.reservation_details}
                                                        </h3>
                                                    </div>
                                                    <div className="p-6">
                                                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                                            <div className="p-4 bg-gray-50 rounded-xl md:col-span-2">
                                                                <span className="block mb-1 text-xs font-semibold tracking-wide text-gray-500 uppercase">{App_text.form_package.package_label}</span>
                                                                <span className="block text-lg font-bold text-gray-900">{card.title}</span>
                                                            </div>

                                                            <div className="p-4 bg-gray-50 rounded-xl">
                                                                <span className="block mb-1 text-xs font-semibold tracking-wide text-gray-500 uppercase">{App_text.form_package.date_label}</span>
                                                                <span className="block font-semibold text-gray-900">
                                                                    {card.type === 'private'
                                                                        ? submittedData?.date
                                                                            ? `${formatDateWithSuffix(submittedData.date)}${submittedData.to_date ? ` to ${formatDateWithSuffix(submittedData.to_date)}` : ''}`
                                                                            : 'N/A'
                                                                        : (() => {
                                                                              const selectedDate = card.dates?.find((date) => date.id.toString() === submittedData?.date?.toString());
                                                                              if (selectedDate) {
                                                                                  return `${formatDateWithSuffix(selectedDate.from_date || selectedDate.date)}${
                                                                                      selectedDate.to_date ? ` to ${formatDateWithSuffix(selectedDate.to_date)}` : ''
                                                                                  }`;
                                                                              }
                                                                              return submittedData?.formattedDate || submittedData?.date;
                                                                          })()}
                                                                </span>
                                                            </div>

                                                            <div className="p-4 bg-gray-50 rounded-xl">
                                                                <span className="block mb-1 text-xs font-semibold tracking-wide text-gray-500 uppercase">{App_text.form_package.travelers_label}</span>
                                                                <span className="block font-semibold text-gray-900">
                                                                    {submittedData?.adults}{' '}
                                                                    {parseInt(submittedData?.adults) === 1 ? App_text.form_package.adult_text : App_text.form_package.adults_text}
                                                                    {parseInt(submittedData?.children) > 0 &&
                                                                        `, ${submittedData?.children} ${
                                                                            parseInt(submittedData?.children) === 1 ? App_text.form_package.child_text : App_text.form_package.children_text
                                                                        }`}
                                                                    {parseInt(submittedData?.infants) > 0 &&
                                                                        `, ${submittedData?.infants} ${
                                                                            parseInt(submittedData?.infants) === 1 ? App_text.form_package.infant_text : App_text.form_package.infants_text
                                                                        }`}
                                                                </span>
                                                            </div>

                                                            {card.starting_points?.length > 0 && (
                                                                <div className="p-4 bg-gray-50 rounded-xl">
                                                                    <span className="block mb-1 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                                                                        {App_text.form_package.starting_point}
                                                                    </span>
                                                                    <span className="block font-semibold text-gray-900">
                                                                        {card.starting_points.map((item, index) => (
                                                                            <Fragment key={index}>
                                                                                {item?.name}
                                                                                {index !== card.starting_points.length - 1 && ' / '}
                                                                            </Fragment>
                                                                        ))}
                                                                    </span>
                                                                </div>
                                                            )}

                                                            {card.duration && (
                                                                <div className="p-4 bg-gray-50 rounded-xl">
                                                                    <span className="block mb-1 text-xs font-semibold tracking-wide text-gray-500 uppercase">{App_text.form_package.duration}</span>
                                                                    <span className="block font-semibold text-gray-900">{card.duration}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Extras for Private */}
                                                {submittedData?.extras?.length > 0 && (
                                                    <div className="overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-lg">
                                                        <div className="flex justify-between items-center p-6 bg-gradient-to-r from-blue-50 border-b border-blue-200 to-blue-100/50">
                                                            <h4 className="flex gap-2 items-center text-lg font-bold text-gray-900">
                                                                <div className="p-2 bg-blue-200 rounded-lg">
                                                                    <FaCheck className="text-blue-700 size-4" />
                                                                </div>
                                                                {App_text.form_package.extra_services}
                                                            </h4>
                                                            <span className="px-4 py-2 text-sm font-bold text-orange-700 bg-orange-100 rounded-full">{App_text.card_tour.text_price}</span>
                                                        </div>
                                                        <div className="p-6 space-y-4">
                                                            {cardExtras
                                                                .filter((extra) => submittedData?.extras?.includes(extra.id))
                                                                .map((extra, index) => {
                                                                    const adultCount = parseInt(submittedData?.extraTravelers?.[extra.id]?.adults || 0);
                                                                    const childCount = parseInt(submittedData?.extraTravelers?.[extra.id]?.children || 0);
                                                                    const infantCount = parseInt(submittedData?.extraTravelers?.[extra.id]?.infants || 0);

                                                                    if (adultCount === 0 && childCount === 0 && infantCount === 0) return null;

                                                                    return (
                                                                        <div key={extra.id} className="p-4 bg-gray-50 rounded-xl">
                                                                            <div className="flex justify-between items-start mb-3">
                                                                                <span className="font-semibold text-gray-900">{extra.name}</span>
                                                                            </div>
                                                                            <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                                                                                {adultCount > 0 && (
                                                                                    <div className="flex gap-1 items-center px-3 py-1 bg-white rounded-full border border-gray-200">
                                                                                        <span className="font-medium">{adultCount}</span> {adultCount === 1 ? 'Adult' : 'Adults'}
                                                                                    </div>
                                                                                )}
                                                                                {childCount > 0 && (
                                                                                    <div className="flex gap-1 items-center px-3 py-1 bg-white rounded-full border border-gray-200">
                                                                                        <span className="font-medium">{childCount}</span> {childCount === 1 ? 'Child' : 'Children'}
                                                                                    </div>
                                                                                )}
                                                                                {infantCount > 0 && (
                                                                                    <div className="flex gap-1 items-center px-3 py-1 bg-white rounded-full border border-gray-200">
                                                                                        <span className="font-medium">{infantCount}</span> {infantCount === 1 ? 'Infant' : 'Infants'}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                {/* Non-Private - Reservation Details */}
                                                <div className="overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-lg">
                                                    <div className="p-6 bg-gradient-to-r from-orange-50 border-b border-orange-200 to-orange-100/50">
                                                        <h3 className="flex gap-2 items-center text-xl font-bold text-gray-900">
                                                            <div className="p-2 bg-orange-200 rounded-lg">
                                                                <FaCheck className="text-orange-700 size-5" />
                                                            </div>
                                                            {App_text.form_package.reservation_details}
                                                        </h3>
                                                    </div>
                                                    <div className="p-6">
                                                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                                            <div className="p-4 bg-gray-50 rounded-xl md:col-span-2">
                                                                <span className="block mb-1 text-xs font-semibold tracking-wide text-gray-500 uppercase">{App_text.form_package.package_label}</span>
                                                                <span className="block text-lg font-bold text-gray-900">{card.title}</span>
                                                            </div>

                                                            <div className="p-4 bg-gray-50 rounded-xl">
                                                                <span className="block mb-1 text-xs font-semibold tracking-wide text-gray-500 uppercase">{App_text.form_package.date_label}</span>
                                                                <span className="block font-semibold text-gray-900">
                                                                    {card.type === 'private'
                                                                        ? submittedData?.date
                                                                            ? formatDateWithSuffix(submittedData?.date)
                                                                            : 'N/A'
                                                                        : (() => {
                                                                              const selectedDate = card.dates?.find((date) => date.id.toString() === submittedData?.date?.toString());
                                                                              if (selectedDate) {
                                                                                  return `${formatDateWithSuffix(selectedDate.from_date || selectedDate.date)}${
                                                                                      selectedDate.to_date ? ` to ${formatDateWithSuffix(selectedDate.to_date)}` : ''
                                                                                  }`;
                                                                              }
                                                                              return submittedData?.formattedDate || submittedData?.date;
                                                                          })()}
                                                                </span>
                                                            </div>

                                                            <div className="p-4 bg-gray-50 rounded-xl">
                                                                <span className="block mb-1 text-xs font-semibold tracking-wide text-gray-500 uppercase">{App_text.form_package.travelers_label}</span>
                                                                <span className="block font-semibold text-gray-900">
                                                                    {submittedData?.adults}{' '}
                                                                    {parseInt(submittedData?.adults) === 1 ? App_text.form_package.adult_text : App_text.form_package.adults_text}
                                                                    {parseInt(submittedData?.children) > 0 &&
                                                                        `, ${submittedData?.children} ${
                                                                            parseInt(submittedData?.children) === 1 ? App_text.form_package.child_text : App_text.form_package.children_text
                                                                        }`}
                                                                    {parseInt(submittedData?.infants) > 0 &&
                                                                        `, ${submittedData?.infants} ${
                                                                            parseInt(submittedData?.infants) === 1 ? App_text.form_package.infant_text : App_text.form_package.infants_text
                                                                        }`}
                                                                </span>
                                                            </div>

                                                            {card.starting_points?.length > 0 && (
                                                                <div className="p-4 bg-gray-50 rounded-xl">
                                                                    <span className="block mb-1 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                                                                        {App_text.form_package.starting_point}
                                                                    </span>
                                                                    <span className="block font-semibold text-gray-900">
                                                                        {card.starting_points.map((item, index) => (
                                                                            <Fragment key={index}>
                                                                                {item?.name}
                                                                                {index !== card.starting_points.length - 1 && ' / '}
                                                                            </Fragment>
                                                                        ))}
                                                                    </span>
                                                                </div>
                                                            )}

                                                            {card.duration && (
                                                                <div className="p-4 bg-gray-50 rounded-xl">
                                                                    <span className="block mb-1 text-xs font-semibold tracking-wide text-gray-500 uppercase">{App_text.form_package.duration}</span>
                                                                    <span className="block font-semibold text-gray-900">{card.duration}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        {/* Additional Comments */}
                                        {submittedData?.message && (
                                            <div className="overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-lg">
                                                <div className="p-6 bg-gradient-to-r from-purple-50 border-b border-purple-200 to-purple-100/50">
                                                    <h3 className="flex gap-2 items-center text-lg font-bold text-gray-900">
                                                        <div className="p-2 bg-purple-200 rounded-lg">
                                                            <FaCheck className="text-purple-700 size-4" />
                                                        </div>
                                                        Additional Comments
                                                    </h3>
                                                </div>
                                                <div className="p-6">
                                                    <p className="leading-relaxed text-gray-700">{submittedData.message}</p>
                                                </div>
                                            </div>
                                        )}

                                        {/* What Happens Next */}
                                        <div className="overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-200 shadow-lg">
                                            <div className="p-6 border-b border-blue-200 bg-white/50">
                                                <h3 className="text-xl font-bold text-gray-900">{App_text.form_package.what_happens_next}</h3>
                                            </div>
                                            <div className="p-6">
                                                <ul className="space-y-4">
                                                    <li className="flex gap-4 items-start">
                                                        <div className="flex-shrink-0 p-2 bg-green-100 rounded-full">
                                                            <FaCheck className="text-main-color size-5" />
                                                        </div>
                                                        <span className="leading-relaxed text-gray-700">{App_text.form_package.next_step_1}</span>
                                                    </li>
                                                    <li className="flex gap-4 items-start">
                                                        <div className="flex-shrink-0 p-2 bg-green-100 rounded-full">
                                                            <FaCheck className="text-main-color size-5" />
                                                        </div>
                                                        <span className="leading-relaxed text-gray-700">{App_text.form_package.next_step_2}</span>
                                                    </li>
                                                    <li className="flex gap-4 items-start">
                                                        <div className="flex-shrink-0 p-2 bg-green-100 rounded-full">
                                                            <FaCheck className="text-main-color size-5" />
                                                        </div>
                                                        <span className="leading-relaxed text-gray-700">{App_text.form_package.next_step_3}</span>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>

                                        {/* Close Button */}
                                        <div className="flex justify-center pt-8 pb-4">
                                            <Dialog.Close asChild>
                                                <button
                                                    type="button"
                                                    className="inline-flex items-center justify-center gap-2 font-semibold text-white rounded-full border-none cursor-pointer transition-all duration-200 hover:opacity-90 hover:-translate-y-0.5"
                                                    style={{
                                                        background: 'linear-gradient(135deg, #E8604C 0%, #313041 100%)',
                                                        boxShadow: '0 4px 14px rgba(232,96,76,0.35)',
                                                        padding: '14px 36px',
                                                        fontSize: '1.05rem',
                                                        height: '56px',
                                                    }}
                                                >
                                                    {App_text.form_package.close_button}
                                                    <RiCloseLine className="size-6" />
                                                </button>
                                            </Dialog.Close>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
